import {
  ActivityDefinition,
  ActivityExpressionContext,
  ActivityFormSnapshot,
  activityStatuses,
  addDays,
  CreateActivityArgs,
  DependencyError,
  evaluateActivityExpression,
  getCurrentActivityExpressionScope,
  getPropertyDisplayValue,
  html,
  icon,
  isActivityEndDateRequired,
  isUnansweredResponse,
  isValidURLResponse,
  keyBy,
  maskToArray,
  pageActivityStatusLabelTemplate,
  parseFormData,
  parseUserDate,
  progressRingIconContent,
  Property,
  PropertyFormat,
  PublicCreateActivityArgs,
  render,
  renderCalculatedFieldDependencyList,
  UpdateActivityArgs,
  userDateFormat,
  walkActivityElements
} from '@cumu/shared';
import { loc_fields } from '@cumu/strings';
import { attr as cattr, controller, target, targets } from '@github/catalyst';
import type IncludeFragmentElement from '@github/include-fragment-element';
import TabContainerElement from '@github/tab-container-element';
import { readCsvFile } from '../csv';
import { readExcelFile } from '../excel';
import { FORM_AFTER_SUBMIT_EVENT_TYPE } from '../forms/behavior-element';
import { allFormData } from '../forms/utilities';
import {
  FormValidationErrorEventDetails,
  validateForm
} from '../forms/validation';
import { getLocale } from '../locale';
import type { AssigneePickerElement } from './assignee-picker';
import {
  InvokeAssistantEvent,
  invokeAssistantEventName
} from './assistant-chat-controller';
import { dispatchChange, focusSomething, scrollIntoViewIfNeeded } from './util';

type EncodedActivityFormSnapshot = Required<
  Pick<UpdateActivityArgs, 'form_snapshot_json' | 'form_snapshot_integrity'>
>;

const locale = getLocale();

const detailsTabState = {};

const printHash = '#:print:';

@controller('activity-controller')
export default class ActivityControllerElement extends HTMLElement {
  // history = new Map<number, Promise<ResponseHistoryItem[]>>();
  @target declare form: HTMLFormElement;
  @target declare elementsContainer: HTMLDivElement;
  @target declare elementsSummaryContainer: HTMLDivElement;
  @target declare creationFields: HTMLDivElement | null;
  @target declare timelineContainer: HTMLDivElement;
  @target declare editCommentTemplate: HTMLTemplateElement;
  @target declare subscribersSection: HTMLDivElement;
  @target declare attachmentsSection: HTMLDivElement;
  @target declare eventEnrollmentLinkSection: HTMLDivElement;
  @target declare eventQuestionnairesSection: HTMLDivElement;
  @target declare railActivitySendingRequests: HTMLDivElement;
  @target declare addAnotherContent: HTMLElement;
  @target declare assigneePicker: AssigneePickerElement;
  @target declare assigneePrintContainer: HTMLElement;
  @targets declare headingNavLinks: HTMLAnchorElement[];
  @targets declare printCommentsItems: HTMLElement[];
  @cattr declare organization_id: string;
  @cattr declare activity_id: string;
  @cattr declare status_assignment_src: string;
  assignmentRuleTimeout = 0;
  inputTimeout = 0;
  location = new URL(location.href);
  detachedAbortController: AbortController | undefined;
  private handleChangeRevision = 0;
  private pendingStatusChange = false;
  private readonly activityFormSnapshotLoads = new Map<
    string,
    Promise<EncodedActivityFormSnapshot>
  >();

  async connectedCallback() {
    this.detachedAbortController = new AbortController();
    if (location.hash.startsWith(printHash)) {
      const behavior = location.hash.substr(printHash.length);
      history.replaceState(undefined, '', location.pathname + location.search);
      let includeFragment: IncludeFragmentElement | null;
      while ((includeFragment = this.querySelector('include-fragment[src]'))) {
        await new Promise(resolve =>
          includeFragment!.addEventListener(
            'include-fragment-replaced',
            resolve,
            { once: true }
          )
        );
      }
      this.doBehavior(behavior);
    }
    this.resolveFieldLinks();
    const clickHandler = async (event: Event) => {
      const behavior =
        (event.target instanceof Element &&
          event.target
            .closest('[activity-behavior]')
            ?.getAttribute('activity-behavior')) ||
        '';
      this.doBehavior(behavior, event);
    };
    addEventListener('click', clickHandler, {
      capture: true,
      signal: this.detachedAbortController.signal
    });

    const popstateHandler = (event: PopStateEvent) => {
      if (event.state === detailsTabState) {
        this.editElements();
      } else {
        const url = new URL(location.href);
        if (
          url.origin + url.pathname + url.search !==
          this.location.origin + this.location.pathname + this.location.search
        ) {
          location.reload();
        }
      }
    };
    addEventListener('popstate', popstateHandler, {
      signal: this.detachedAbortController.signal
    });
  }

  disconnectedCallback() {
    this.detachedAbortController?.abort();
    this.detachedAbortController = undefined;
  }

  get newCommentTextArea() {
    return this.form.elements.namedItem('comment_body') as HTMLTextAreaElement;
  }

  get formBehavior() {
    return this.form.querySelector('form-behavior');
  }

  async doBehavior(behavior: string, event?: Event) {
    switch (behavior) {
      case 'edit-details': {
        event?.preventDefault();
        const focusTarget =
          (event?.target instanceof HTMLAnchorElement &&
            event.target.getAttribute('href')?.startsWith('#') &&
            document.getElementById(
              event.target.getAttribute('href')!.substr(1)
            )) ||
          null;
        this.editElements(focusTarget);
        return;
      }
      case 'print-summary':
      case 'print-summary-with-comments':
      case 'print-details': {
        if (location.pathname.endsWith('/new')) {
          await new Promise(resolve =>
            this.addEventListener(FORM_AFTER_SUBMIT_EVENT_TYPE, resolve, {
              once: true
            })
          );
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        const { origin, pathname, search } = location;
        const actualIsDetails = pathname.endsWith('/details');
        const requestedIsDetails = behavior === 'print-details';
        if (actualIsDetails === requestedIsDetails) {
          this.printCommentsItems.forEach(el =>
            el.classList.toggle(
              'd-print-none',
              behavior !== 'print-summary-with-comments'
            )
          );
          print();
          addEventListener('afterprint', () =>
            this.printCommentsItems.forEach(el =>
              el.classList.add('d-print-none')
            )
          );
        } else {
          const requestedPathname = actualIsDetails
            ? pathname.substr(0, pathname.length - '/details'.length)
            : pathname + '/details';
          location.href = `${origin}${requestedPathname}${search}${printHash}${behavior}`;
        }
        return;
      }
      default:
        if (behavior) {
          throw new Error(`Unexpected activity-behavior "${behavior}".`);
        }
    }
  }

  handleValidationError(event: CustomEvent<FormValidationErrorEventDetails>) {
    if (
      event.detail.group.hasAttribute('form-element') &&
      event.detail.group.classList.contains('form-element--skipped')
    ) {
      event.preventDefault();
    } else {
      this.editElements();
    }
  }

  handleServerValidationError() {
    this.editElements();
  }

  editElements = (elementToFocus?: Element | null) => {
    if (this.elementsContainer?.hidden) {
      this.elementsContainer.hidden = false;
      this.elementsSummaryContainer.remove(); // todo: fix duplicate heading ids
      history.pushState(
        detailsTabState,
        '',
        location.pathname + '/details' + this.location.search
      );
      this.location = new URL(location.href);
      document
        .querySelector('.UnderlineNav-item[aria-current="page"]')
        ?.setAttribute('aria-current', 'false');
      document
        .querySelector('.UnderlineNav-item[href$="/details"]')
        ?.setAttribute('aria-current', 'page');
    }
    if (elementToFocus) {
      elementToFocus.scrollIntoView({
        block:
          elementToFocus.clientHeight > window.innerHeight * 0.8
            ? 'start'
            : 'center'
      });
    }
    focusSomething(elementToFocus ?? this.elementsContainer);
    if (
      elementToFocus?.parentElement instanceof HTMLDetailsElement &&
      !elementToFocus.parentElement.open
    ) {
      elementToFocus.parentElement.open = true;
      elementToFocus.parentElement.dispatchEvent(
        new ToggleEvent('toggle', {
          bubbles: true,
          newState: 'open',
          oldState: 'closed'
        })
      );
    }
  };

  focusElement(event: Event) {
    event.preventDefault();
    const idish =
      event.currentTarget instanceof Element &&
      event.currentTarget.getAttribute('element-identifier');
    let el = this.querySelector(
      `[form-element="${idish}"],[name="${idish}"],[name^="${idish}:"],[id="${idish}"]`
    );
    if (el instanceof HTMLInputElement && el.type === 'hidden') {
      el = el.parentElement;
    }
    this.editElements(el);
    el?.closest('.form-group')?.classList.add(
      'element-changed-animation--fast'
    );
    return el;
  }

  focusElementAndOpenModal(event: Event) {
    const el = this.focusElement(event);
    el?.querySelector('modal-controller')?.open();
  }

  assignToMe() {
    this.editElements();
    this.assigneePicker.assignToMeAndSave();
  }

  async activitySaved(event: CustomEvent<Response>) {
    // Reset comment textarea and its preview tab.
    const scrollNewCommentIntoView =
      this.newCommentTextArea.value.trim() !== '' &&
      this.newCommentTextArea.defaultValue === '';
    this.newCommentTextArea.value = '';
    dispatchChange(this.newCommentTextArea);
    this.querySelectorAll<TabContainerElement>(
      'markdown-editor tab-container'
    ).forEach(el => {
      if (el.tabIndex! === 0) {
        el.selectTab(0);
      }
    });

    // Switch to PUT mode body
    if (this.creationFields) {
      this.creationFields.remove();
    }

    // Previous steps will have artifically dirtied the form... accept those changes.
    this.form.querySelector('form-behavior')?.acceptChanges();

    // Remove process flash (assumes save completed the process)
    document.getElementById('process-flash')?.remove();

    const html = await event.detail.text();
    this.timelineContainer.innerHTML = html;
    const subscribersContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="subscribers-content"]'
      );
    if (subscribersContent) {
      this.subscribersSection.replaceWith(subscribersContent.content);
    }
    const attachmentsContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="attachments-content"]'
      );
    if (attachmentsContent) {
      this.attachmentsSection.replaceWith(attachmentsContent.content);
    }
    const eventEnrollmentLinkContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="event-enrollment-link-content"]'
      );
    if (eventEnrollmentLinkContent) {
      this.eventEnrollmentLinkSection?.replaceWith(
        eventEnrollmentLinkContent.content
      );
    }
    const eventQuestionnairesContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="event-questionnaires-content"]'
      );
    if (eventQuestionnairesContent) {
      this.eventQuestionnairesSection?.replaceWith(
        eventQuestionnairesContent.content
      );
    }
    const railActivitySendingRequestsContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="rail-activity-sending-requests-content"]'
      );
    if (railActivitySendingRequestsContent) {
      this.railActivitySendingRequests?.replaceWith(
        railActivitySendingRequestsContent.content
      );
    }
    const tabNavContent =
      this.timelineContainer.querySelector<HTMLTemplateElement>(
        '[id="activity-tabnav-content"]'
      );
    if (tabNavContent) {
      document
        .getElementById('header-tabnav')
        ?.replaceWith(tabNavContent.content);
    }

    this.timelineContainer
      .querySelectorAll<HTMLTemplateElement>('[id^="attachment-question-"]')
      .forEach(template => {
        this.querySelector(
          `[form-element="${template.id.substr('attachment-question-'.length)}"]`
        )?.replaceWith(template.content);
      });

    this.addAnotherContent?.removeAttribute('hidden');

    if (scrollNewCommentIntoView) {
      const item = this.timelineContainer.querySelector(
        '.TimelineItem-body:has(.markdown-body)'
      );
      if (item) {
        // Scroll on next tick because scrolling immediately will interfere with requires-submit link clicks.
        setTimeout(scrollIntoViewIfNeeded, 0, item);
      }
    }

    // Previous steps will have artifically dirtied the form... accept those changes.
    this.form.querySelector('form-behavior')?.acceptChanges();
  }

  statusFocus({ target }: Event) {
    // clear status id from hash after it receives focus.
    if (target instanceof Element && target.id === location.hash.substr(1)) {
      history.replaceState(undefined, '', location.pathname + location.search);
    }
  }

  recommendationValueInputFocused({ target }: Event) {
    target instanceof Element &&
      target
        .closest('.form-group')!
        .querySelector<HTMLElement>('button,a')!
        .focus();
  }

  async subscriptionUpdated(event: CustomEvent<Response>) {
    const html = await event.detail.text();
    const template = document.createElement('template');
    template.innerHTML = html;
    this.subscribersSection.replaceWith(template.content);
  }

  async editComment(event: Event) {
    const item = this.getTimelineItem(event);
    if (!item) {
      return;
    }
    const markdownContent = item.querySelector('.markdown-body')!;
    const activity_comment_id = markdownContent.getAttribute(
      'activity_comment_id'
    );
    const markdown = markdownContent.getAttribute('source-markdown') ?? '';
    const fragment = this.editCommentTemplate.content.cloneNode(
      true
    ) as DocumentFragment;
    const form = fragment.firstElementChild as HTMLFormElement;
    form.action = `/api/organizations/${this.organization_id}/activities/${this.activity_id}/comments/${activity_comment_id}?_method=PUT`;
    const bodyTextArea = form.elements.namedItem('body') as HTMLTextAreaElement;
    bodyTextArea.value = markdown;
    item.append(fragment);
    item.querySelector<HTMLElement>('.TimelineItem-body')!.hidden = true;
    await new Promise(resolve => setTimeout(resolve));
    scrollIntoViewIfNeeded(bodyTextArea);
    bodyTextArea.focus();
  }

  keyDown(event: KeyboardEvent) {
    // When a user presses tab after editing an number/text input and the
    // subsequent field becomes visible due to conditional logic,
    // focus will skip the newly visible field.
    // The following code forces evaluation of the form state early to circumvent the issue.
    if (
      event.key === 'Tab' &&
      document.activeElement instanceof HTMLInputElement &&
      document.activeElement.type === 'number'
    ) {
      this.handleChange(event);
    }
  }

  editCommentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelCommentEdit(event);
    }
  }

  cancelCommentEdit(event: Event) {
    const item = this.getTimelineItem(event);
    if (!item) {
      return;
    }
    const form =
      event.target instanceof Element && event.target.closest('form');
    if (form) {
      form.remove();
    }
    item.querySelector<HTMLElement>('.TimelineItem-body')!.hidden = false;
  }

  async deleteComment(event: Event) {
    const item = this.getTimelineItem(event);
    if (!item) {
      return;
    }
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    const markdownContent = item.querySelector('.markdown-body')!;
    const activity_comment_id = markdownContent.getAttribute(
      'activity_comment_id'
    );
    const response = await fetch(
      `/api/organizations/${this.organization_id}/activities/${this.activity_id}/comments/${activity_comment_id}`,
      {
        method: 'DELETE'
      }
    );
    if (response.ok) {
      item.remove();
    }
  }

  async quoteReply(event: Event) {
    const item = this.getTimelineItem(event);
    if (!item) {
      return;
    }
    const markdown =
      item.querySelector('.markdown-body')?.getAttribute('source-markdown') ??
      '';
    const prompt = `[enter your reply here]`;
    const blockquote = `> ${markdown.split(/\r?\n/).join('\n> ')}\n\n`;
    const start = blockquote.length;
    this.newCommentTextArea.value = blockquote + prompt;
    dispatchChange(this.newCommentTextArea);
    await new Promise(resolve => setTimeout(resolve));
    this.newCommentTextArea.scrollIntoView({ behavior: 'smooth' });
    this.newCommentTextArea.focus();
    this.newCommentTextArea.setSelectionRange(
      start,
      this.newCommentTextArea.value.length
    );
  }

  getTimelineItem(event: Event) {
    return (
      event.target instanceof Element &&
      event.target.closest<HTMLElement>('.TimelineItem')
    );
  }

  _elementsAndProperties:
    | (Pick<ActivityFormSnapshot, 'elements' | 'currentUser' | 'scopeData'> & {
        activityDefinition: Pick<
          ActivityDefinition,
          'default_status' | 'type' | 'duration_days'
        >;
        propertyMap: Record<number, Property>;
      })
    | undefined;

  getElementsAndProperties(json: string) {
    if (!this._elementsAndProperties) {
      const {
        elements,
        properties,
        activityDefinition,
        currentUser,
        scopeData
      } = JSON.parse(json) as ActivityFormSnapshot;
      this._elementsAndProperties = {
        activityDefinition,
        elements,
        propertyMap: keyBy<Property, 'property_id'>(properties, 'property_id'),
        currentUser,
        scopeData
      };
    }
    return this._elementsAndProperties;
  }

  private loadActivityFormSnapshot(
    form_snapshot_json: string,
    form_snapshot_integrity: string,
    referenced_activity_ids: string[]
  ) {
    const key = `${form_snapshot_integrity}:${referenced_activity_ids.join(',')}`;
    let loading = this.activityFormSnapshotLoads.get(key);
    if (!loading) {
      loading = (async () => {
        const response = await fetch(
          `/api/organizations/${this.organization_id}/activity-expression-definition`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              form_snapshot_json,
              form_snapshot_integrity,
              referenced_activity_ids
            }),
            signal: this.detachedAbortController?.signal
          }
        );
        if (!response.ok) {
          throw new DependencyError(response, await response.text());
        }
        return (await response.json()) as EncodedActivityFormSnapshot;
      })().finally(() => {
        this.activityFormSnapshotLoads.delete(key);
      });
      this.activityFormSnapshotLoads.set(key, loading);
    }
    return loading;
  }

  handleInput(event: Event) {
    const { target } = event;
    if (
      (target instanceof HTMLInputElement &&
        ['text', 'number'].includes(target.type)) ||
      target instanceof HTMLTextAreaElement
    ) {
      clearTimeout(this.inputTimeout);
      this.inputTimeout = setTimeout(() => this.handleChange(event), 300);
    }
  }

  nextEventIndex = 0;
  log(event: Event, ...args: any[]) {
    if (new URLSearchParams(location.search).has('debug')) {
      const name =
        (event.target instanceof Element &&
          event.target.getAttribute('name')) ||
        '(name missing)';
      (event as any).__log_index__ ??= this.nextEventIndex++;
      const index = (event as any).__log_index__;
      console.info(`Handle ${event.type} #${index} on ${name}`, ...args);
    }
  }

  async handleChange(event: Event): Promise<void> {
    const revision = ++this.handleChangeRevision;
    this.log(event, 'Enter handleChange');
    try {
      clearTimeout(this.inputTimeout);
      const formData = allFormData(this.form);
      const isModal = this.closest('modal-content');
      if (
        event.type === 'change' &&
        event.target instanceof HTMLInputElement &&
        event.target.name === 'status'
      ) {
        this.pendingStatusChange = true;
      }

      const parsed = parseFormData<
        (
          | CreateActivityArgs
          | UpdateActivityArgs
          | (PublicCreateActivityArgs & {
              status: undefined;
              start_date: undefined;
              end_date: undefined;
            })
        ) &
          EncodedActivityFormSnapshot
      >(formData);
      const {
        responses = {},
        form_snapshot_json,
        form_snapshot_integrity,
        status,
        start_date,
        end_date
      } = parsed;
      const {
        activityDefinition: { type, duration_days },
        elements,
        propertyMap,
        currentUser,
        scopeData
      } = this.getElementsAndProperties(form_snapshot_json);
      const missingActivityIds = new Set<string>();
      for (const property of Object.values(propertyMap)) {
        const value = responses[`p${property.property_id}`];
        if (
          property.type === 'activity' &&
          typeof value === 'string' &&
          value &&
          !scopeData.activities[value]
        ) {
          missingActivityIds.add(value);
        }
      }
      if (missingActivityIds.size > 0) {
        const loading = this.loadActivityFormSnapshot(
          form_snapshot_json,
          form_snapshot_integrity,
          Array.from(missingActivityIds).sort()
        );
        this.formBehavior?.setBusy(loading);
        const encoded = await loading;
        if (revision !== this.handleChangeRevision) {
          return;
        }
        const formSnapshotJsonInput =
          this.form.elements.namedItem('form_snapshot_json');
        const formSnapshotIntegrityInput = this.form.elements.namedItem(
          'form_snapshot_integrity'
        );
        if (
          !(formSnapshotJsonInput instanceof HTMLInputElement) ||
          !(formSnapshotIntegrityInput instanceof HTMLInputElement)
        ) {
          throw new Error('Unable to locate activity form snapshot inputs.');
        }
        formSnapshotJsonInput.value = encoded.form_snapshot_json;
        formSnapshotIntegrityInput.value = encoded.form_snapshot_integrity;
        this._elementsAndProperties = undefined;
        await this.handleChange(event);
        return;
      }

      const statusChanged = this.pendingStatusChange;
      this.pendingStatusChange = false;

      if (statusChanged && status) {
        // defer validation until after questions have been updated with required status.
        clearTimeout(this.assignmentRuleTimeout);
        this.assignmentRuleTimeout = setTimeout(() => {
          validateForm(this.form, 'info');

          this.formBehavior!.setBusy(this.applyAssignmentRule(formData));

          if (!isModal) {
            const statusLabel = document.getElementById(
              'activity-status-label'
            )!;
            statusLabel.insertAdjacentHTML(
              'afterend',
              render(pageActivityStatusLabelTemplate(status, locale))
            );
            statusLabel.remove();
          }
        }, 0);
      }

      const currentScope = getCurrentActivityExpressionScope(scopeData);
      currentScope.responses = responses;
      if (status) {
        currentScope.activity.status = status;
      }
      currentScope.activity.start_date =
        start_date ?? new Date().toISOString().substr(0, 10);
      currentScope.activity.end_date = end_date ?? null;
      if (this.assigneePicker) {
        currentScope.activity.assignee_organization =
          this.assigneePicker.value?.organization_login ?? null;
        currentScope.activity.assignee_user =
          this.assigneePicker.value?.user_login ?? null;
        currentScope.activity.assignee_user_first_name =
          this.assigneePicker.value?.user_first_name ?? null;
        currentScope.activity.assignee_user_last_name =
          this.assigneePicker.value?.user_last_name ?? null;
      }
      const primaryPhone =
        'phones' in parsed ? parsed.phones.find(x => x.is_primary) : null;
      currentScope.activity.primary_phone_number = primaryPhone?.number ?? null;
      const primaryEmail =
        'emails' in parsed ? parsed.emails.find(x => x.is_primary) : null;
      currentScope.activity.primary_email_address = primaryEmail?.email ?? null;
      const primaryPhysical =
        'addresses' in parsed
          ? parsed.addresses.find(x => x.is_primary_physical)
          : null;
      currentScope.activity.primary_physical_street_address =
        primaryPhysical?.street_address ?? null;
      currentScope.activity.primary_physical_sub_address =
        primaryPhysical?.sub_address ?? null;
      currentScope.activity.primary_physical_city =
        primaryPhysical?.city ?? null;
      currentScope.activity.primary_physical_state =
        primaryPhysical?.region_abbr ?? null;
      currentScope.activity.primary_physical_zip =
        primaryPhysical?.postal_code ?? null;
      currentScope.activity.primary_physical_zip_ext =
        primaryPhysical?.postal_code_ext ?? null;
      currentScope.activity.primary_physical_subregion =
        primaryPhysical?.subregion ?? null;
      const expressionContext = ActivityExpressionContext.forActivity({
        currentUser,
        properties: propertyMap,
        scopeData,
        operation: 'view'
      });

      const startDateInput = this.form.elements.namedItem('start_date:date');
      const endDateInput = this.form.elements.namedItem('end_date:date');
      const dueDateInput = this.form.elements.namedItem('due_date:date');

      // Set end date required on status change.
      // Set end date value based on whether the status is opened or closed.
      if (
        statusChanged &&
        endDateInput instanceof HTMLInputElement &&
        !endDateInput.disabled
      ) {
        const required = isActivityEndDateRequired(
          type,
          status ?? currentScope.activity.status
        );
        endDateInput.setAttribute('validate-required', String(required));
        const newEndDateValue = required
          ? userDateFormat.format(new Date())
          : '';
        if (!!endDateInput.value !== !!newEndDateValue) {
          endDateInput.classList.remove('element-changed-animation');
          endDateInput.value = newEndDateValue;
          dispatchChange(endDateInput);
          setTimeout(
            () => endDateInput.classList.add('element-changed-animation'),
            0
          );
          this.log(event, 'Exit handleChange - end date changed');
          return; // dispatchChange will trigger handleChange again.
        }
      }

      // if start date or end date changed, and end date is before start date, update the date to match
      if (
        event.type === 'change' &&
        startDateInput instanceof HTMLInputElement &&
        endDateInput instanceof HTMLInputElement &&
        endDateInput.value &&
        // document.activeElement !== startDateInput &&
        // document.activeElement !== endDateInput &&
        (event.target === startDateInput || event.target === endDateInput) &&
        !endDateInput.disabled
      ) {
        const startDate = parseUserDate(startDateInput.value);
        const endDate = parseUserDate(endDateInput.value);
        if (startDate && endDate && startDate > endDate) {
          const input =
            event.target === startDateInput ? endDateInput : startDateInput;
          const value = event.target === startDateInput ? startDate : endDate;
          input.classList.remove('element-changed-animation');
          input.value = userDateFormat.format(value);
          dispatchChange(input);
          setTimeout(() => input.classList.add('element-changed-animation'), 0);
          this.log(event, 'Exit handleChange - start or end date changed');
          return; // dispatchChange will trigger handleChange again.
        }
      }

      // if start date changed, update due date
      if (
        event.type === 'change' &&
        startDateInput instanceof HTMLInputElement &&
        dueDateInput instanceof HTMLInputElement &&
        // document.activeElement !== startDateInput &&
        // document.activeElement !== dueDateInput &&
        event.target === startDateInput &&
        !startDateInput.disabled
      ) {
        const startDate = parseUserDate(startDateInput.value);
        if (startDate) {
          const value = userDateFormat.format(
            addDays(startDate, duration_days)
          );
          if (dueDateInput.value !== value) {
            dueDateInput.classList.remove('element-changed-animation');
            dueDateInput.value = value;
            dispatchChange(dueDateInput);
            setTimeout(
              () => dueDateInput.classList.add('element-changed-animation'),
              0
            );
            this.log(event, 'Exit handleChange - due date changed');
            return; // dispatchChange will trigger handleChange again.
          }
        }
      }

      let visibleProperties = formData.has('title') ? 1 : 0;
      let answeredProperties = formData.get('title') ? 1 : 0;
      let changed: HTMLInputElement | null = null;
      walkActivityElements(
        elements,
        expressionContext,
        (el, visible, currentlyRequired) => {
          if (changed) {
            return;
          }
          if (visible && el.type === 'property') {
            visibleProperties++;
            if ((responses[`p${el.property_id}`] ?? '') !== '') {
              answeredProperties++;
            }
          }
          const element = this.form.querySelector(
            `[form-element="${el.index}"]`
          );
          if (!element) {
            if (el.type === 'recommendation' || el.type === 'event-activity') {
              return;
            }
            throw new Error(
              `Unable to locate element corresponding to "${el.index}" ("${el.type}").`
            );
          }

          const summaryElements = this.querySelectorAll<HTMLElement>(
            `[summary-element="${el.index}"]`
          );

          // show/hide elements based on their visibility expression.
          summaryElements.forEach(el => el.toggleAttribute('hidden', !visible));
          element.classList.toggle('form-element--skipped', !visible);
          if (el.type === 'heading') {
            const navLink = this.headingNavLinks.find(
              x => x.getAttribute('element') === el.index.toString()
            );
            if (navLink) {
              navLink.hidden = !visible;
            }
          }

          // synchronize required state.
          element.setAttribute(
            'validate-required',
            String(currentlyRequired && visible)
          );

          if (el.type !== 'property') {
            return;
          }

          const property = propertyMap[el.property_id!];
          if (!property) {
            throw new Error(
              `Unable to get property with id "${el.property_id}" from property array.`
            );
          }
          const { property_id, type, calculated, expression, format } =
            property;

          // clear data from skipped fields.
          if (!visible) {
            if (responses[`p${property_id}`] !== undefined) {
              if (
                responses[`p${property_id}`] !== '' &&
                responses[`p${property_id}`] !== null
              ) {
                this.log(
                  event,
                  `Clearing ${property.label} (${property_id}) - current value ${JSON.stringify(responses[`p${property_id}`])}`
                );
              }
              setActivityElementValue(element, property, undefined);
              delete responses[`p${property_id}`];
            }
            return;
          }

          // update calculated fields.
          if (!calculated || !expression) {
            return;
          }

          let value = evaluateActivityExpression(
            type,
            expression,
            expressionContext
          );
          if (responses[`p${property_id}`] !== value) {
            this.log(
              event,
              `Calculated field ${property.label} (${property_id}) changed to ${value}`
            );
            changed = setActivityElementValue(element, property, value);
            this.log(event, 'Changed input', changed);
            responses[`p${property_id}`] = value;
            const outputElement = element.querySelector('[calculated-value]')!;
            if (
              format === PropertyFormat.URL ||
              format === PropertyFormat.RelativeURL
            ) {
              if (isValidURLResponse({ value, format })) {
                outputElement.ariaDisabled = 'false';
                outputElement.setAttribute('href', value as string);
              } else {
                outputElement.ariaDisabled = 'true';
                outputElement.removeAttribute('href');
              }
            } else {
              const unanswered = isUnansweredResponse(value);
              outputElement.classList.toggle('text-mono', !unanswered);
              outputElement.classList.toggle('text-italic', unanswered);
              outputElement.classList.toggle('color-fg-muted', unanswered);
              outputElement.textContent = getPropertyDisplayValue({
                locale,
                property,
                value,
                showScore: false,
                activities: []
              });
              document
                .querySelector(`clipboard-copy[for="${outputElement.id}"]`)
                ?.classList.toggle('v-hidden', unanswered);
            }
          } else {
            this.log(
              event,
              `Calculated field ${property.label} (${property_id}) remained ${value}`
            );
          }
          const detailsContent = render(
            renderCalculatedFieldDependencyList(
              getLocale(),
              responses,
              propertyMap,
              element.getAttribute('references')?.split(',').map(Number) ?? []
            )
          );
          const detailsListElement = element.querySelector('details ul')!;
          if (detailsListElement.innerHTML !== detailsContent) {
            detailsListElement.innerHTML = detailsContent;
          }
        }
      );

      if (changed) {
        this.log(
          event,
          `Displatching change for ${(changed as any).getAttribute('name')}`
        );
        dispatchChange(changed);
        return;
      }

      if (!isModal) {
        const progressLabel = document.getElementById(
          'activity-progress-label'
        );
        if (progressLabel) {
          progressLabel.innerHTML = render(
            html`${icon(
              progressRingIconContent(answeredProperties / visibleProperties),
              'f5 color-fg-accent'
            )}
            ${answeredProperties} of ${visibleProperties} ${loc_fields[locale]}`
          );
        }
      }
    } finally {
      this.log(event, 'Exit handleChange');
    }
  }

  applyAssignmentRule = async (body: FormData) => {
    this.assigneePicker.summary.classList.remove('btn-updated');
    const controller = new AbortController();
    setTimeout(() => controller.abort('timeout'), 5000);
    const url = new URL(this.status_assignment_src, location.origin);
    url.searchParams.set(
      'contract_id',
      (this.form.elements.namedItem('contract_id') as HTMLInputElement).value
    );
    const response = await fetch(`${url.pathname}${url.search}`, {
      method: 'POST',
      body,
      signal: controller.signal
    });
    if (!response.ok) {
      throw new DependencyError(response);
    }
    if (response.status !== 200) {
      return;
    }
    const html = await response.text();
    this.assigneePicker.summary.querySelector('[data-menu-button]')!.innerHTML =
      html;
    this.assigneePicker.select();
    this.assigneePicker.summary.classList.add('btn-updated');
    const subscribersTemplate =
      this.assigneePicker.querySelector<HTMLTemplateElement>(
        '#automatic-subscribers'
      )!;
    (this.form.elements.namedItem('subscribers:json') as Element).replaceWith(
      subscribersTemplate.content
    );
    const contractsTemplate =
      this.assigneePicker.querySelector<HTMLTemplateElement>(
        '#assignment-rule-contract'
      )!;
    this.assigneePicker.contractFormGroupContainer.innerHTML = '';
    this.assigneePicker.contractFormGroupContainer.append(
      contractsTemplate.content
    );
  };

  private busyUploadingVaccineSurvey = false;
  async handleRailKeydown({ key, shiftKey, target }: KeyboardEvent) {
    if (
      this.busyUploadingVaccineSurvey ||
      !shiftKey ||
      key !== '?' ||
      !(target instanceof HTMLAnchorElement) ||
      !target.getAttribute('href')?.startsWith('/attachments/')
    ) {
      return;
    }
    const associatedTab = document.querySelector(
      '.UnderlineNav-item[href$="/associated"]'
    );
    if (!associatedTab) {
      throw new Error('Unable to find associated tab.');
    }
    const counter = associatedTab.querySelector('.Counter');
    if (counter?.textContent?.trim()?.replace(/^0$/g, '')) {
      if (
        !confirm(
          `PRESS CANCEL! Event already has ${counter.textContent.trim()} associated activities. Only press OK if you are sure you want to upload a new set of vaccine surveys.`
        )
      ) {
        return;
      }
    }
    this.busyUploadingVaccineSurvey = true;
    target.classList.add('btn-busy');
    try {
      const name = target.title;
      const form = this.closest('activity-controller')!.form;
      const attachmentResponse = await fetch(target.href + '?noredirect');
      if (!attachmentResponse.ok) {
        throw new DependencyError(
          attachmentResponse,
          await attachmentResponse.text()
        );
      }
      const result = name.endsWith('.csv')
        ? await readCsvFile(await attachmentResponse.text(), name, form)
        : await readExcelFile(
            await attachmentResponse.arrayBuffer(),
            name,
            form
          );

      if (result.error || result.data === null || !Array.isArray(result.data)) {
        throw new Error(`Unable to read data from ${name}.`);
      } else if (result.data.length === 0) {
        throw new Error(`No data found in ${name}.`);
      }
      console.table(result.data);
      const response = await fetch(
        `/api/organizations/${this.organization_id}/activities/${this.activity_id}/upload-vaccine-surveys`,
        {
          method: 'POST',
          body: JSON.stringify(result.data),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) {
        throw new DependencyError(response, await response.text());
      }
      const errors = await response.json();
      if (errors.length) {
        console.table(errors);
        throw new Error('Data validation errors. See console for details.');
      } else {
        alert(`Vaccine surveys uploaded.`);
      }
    } catch (err) {
      this.busyUploadingVaccineSurvey = false;
      alert(err);
      throw err;
    } finally {
      target.classList.remove('btn-busy');
      if (this.busyUploadingVaccineSurvey) {
        target.textContent = 'Refresh page & confirm count before proceeding';
        target.classList.add('color-fg-danger');
        target.removeAttribute('href');
      }
    }
  }

  resolveFieldLinks() {
    document
      .querySelectorAll<HTMLAnchorElement>('[href^="#field:"]')
      .forEach(a => {
        const property_id = a.getAttribute('href')!.substr('#field:'.length);
        const control = this.form.querySelector<HTMLElement>(
          [
            `[name="responses/p${property_id}"][id]:not([type="hidden"])`,
            `[name^="responses/p${property_id}:"][id]:not([type="hidden"])`,
            `[calculated-value^="responses/p${property_id}:"]`
          ].join(',')
        );
        if (!control) {
          console.log(`no input for ${property_id}`);
          return;
        }
        const group = control.closest<HTMLElement>('.form-group');
        if (!group) {
          console.log(`no group for ${property_id}`);
          return;
        }
        control.style.scrollMarginTop = `${Math.max(control.offsetTop - group.offsetTop, 60)}px`;
        a.href = `#${control.id}`;
        if (this.elementsContainer?.hidden) {
          a.addEventListener('click', () => {
            this.editElements(control);
          });
        }
      });

    document
      .querySelectorAll<HTMLAnchorElement>(
        '[href^="#associated:person"],[href^="#associated:parent-person-details"],[href^="#associated:parent-person"],[href^="#associated:person-details"],[href^="#associated:parent-activity"]'
      )
      .forEach(a => {
        const association = a
          .getAttribute('href')!
          .substr('#associated:'.length);
        switch (association) {
          case 'person':
            a.href = this.dataset.person_href ?? '';
            break;
          case 'person-details':
            a.href = (this.dataset.person_href ?? '') + '/details';
            break;
          case 'parent-person':
          case 'parent-person-details':
            a.href = this.dataset.parent_person_href
              ? this.dataset.parent_person_href +
                (association === 'parent-person-details' ? '/details' : '')
              : a.href;
            break;
          case 'parent-activity':
            a.href = this.dataset.parent_activity_href || a.href;
            break;
        }
      });

    document
      .querySelectorAll<HTMLAnchorElement>('[href^="#status:"]')
      .forEach(a => {
        const status = a.getAttribute('href')!.substr('#status:'.length);
        if (!activityStatuses.some(x => x.id === status)) {
          return;
        }
        a.role = 'button';
        a.addEventListener('click', event => {
          event.preventDefault();
          this.editElements();
          const radios = this.form.elements.namedItem(
            'status'
          ) as RadioNodeList;
          for (let i = radios.length - 1; i >= 0; i--) {
            const radio = radios.item(i) as HTMLInputElement;
            if (radio.value === status) {
              radio.click();
              break;
            }
          }
        });
      });

    document
      .querySelectorAll<HTMLAnchorElement>('[href^="#print:"]')
      .forEach(a => {
        const behavior = a.getAttribute('href')!.substr('#print:'.length);
        if (
          !['summary', 'summary-with-comments', 'details', 'default'].includes(
            behavior
          )
        ) {
          return;
        }
        a.role = 'button';
        if (behavior === 'default') {
          a.addEventListener('click', event => {
            event.preventDefault();
            print();
          });
        } else {
          a.setAttribute('activity-behavior', `print-${behavior}`);
          a.toggleAttribute('data-requires-submit', true);
          a.innerHTML = render(html`<span>${a.textContent}</span>`);
          a.addEventListener('click', event => {
            event.preventDefault();
          });
        }
      });

    document
      .querySelectorAll<HTMLAnchorElement>('[href^="#assistant:"]')
      .forEach(a => {
        const prompt = decodeURIComponent(
          a.getAttribute('href')!.substr('#assistant:'.length)
        );
        a.role = 'button';
        a.addEventListener('click', event => {
          event.preventDefault();
          const invokeEvent: InvokeAssistantEvent = new CustomEvent(
            invokeAssistantEventName,
            {
              detail: { prompt },
              bubbles: true
            }
          );
          dispatchEvent(invokeEvent);
        });
      });
  }
}

function setActivityElementValue(
  element: Element,
  property: Property,
  value: any
): HTMLInputElement | null {
  let changed: HTMLInputElement | null = null;
  if (
    !property.calculated &&
    property.type === 'int' &&
    property.choice_type !== 'none'
  ) {
    const inputs = element.querySelectorAll<
      HTMLInputElement | HTMLSelectElement
    >(
      `[name="responses/p${property.property_id}"],[name^="responses/p${property.property_id}:"]`
    );
    let checkedFn: (input: HTMLInputElement) => boolean;
    if (property.choice_type === 'multiple') {
      if (typeof value === 'number') {
        value = maskToArray(value);
      }
      if (Array.isArray(value)) {
        checkedFn = x =>
          (x.checked = value.find((v: number) => String(v) === x.value));
      } else {
        checkedFn = x => (x.checked = x.value === '');
      }
    } else {
      checkedFn = x => (x.checked = x.value === String(value));
    }
    inputs.forEach(x => {
      if (x instanceof HTMLSelectElement) {
        x.value = String(value ?? '');
        return;
      }
      const checked = checkedFn(x);
      if (checked !== x.checked) {
        changed = x;
        x.checked = checked;
      }
    });
  } else {
    const input = element.querySelector<HTMLInputElement>(
      `[name="responses/p${property.property_id}"],[name^="responses/p${property.property_id}:"]`
    )!;
    if (property.type === 'date') {
      if (value) {
        value = new Date(value);
      }
      if (value instanceof Date) {
        value = userDateFormat.format(value);
      }
    }
    value = (value ?? '').toString();
    if (input.value !== value) {
      changed = input;
      input.value = value;
    }
  }
  return changed;
}

declare global {
  interface Window {
    ActivityControllerElement: typeof ActivityControllerElement;
  }
  interface HTMLElementTagNameMap {
    'activity-controller': ActivityControllerElement;
  }
}
